/**
 * Provider identification — the single source of truth for who runs this site.
 *
 * Slovenian and EU law require these details to be published in an "easily,
 * directly and permanently accessible" form:
 *   - ZEPT (Zakon o elektronskem poslovanju na trgu), 6. člen — information
 *     society service providers must publish name, seat, contact details, the
 *     register they are entered in with the registration number, and the VAT ID.
 *   - ZGD-1, 45. člen — company data on the company's web pages.
 *   - ZVPot-1, 26./130. člen — pre-contractual trader identification.
 *
 * Every consumer of this object must skip empty strings rather than print them,
 * so an unfilled field degrades to "line omitted" instead of a visible blank
 * label. `TODO(legal)` marks values that must be filled in from the AJPES
 * business register entry before this is considered complete.
 */
export const COMPANY = {
  /** Full registered name as entered in the business register. */
  legalName: "Noventis, trgovina in storitve d.o.o.",
  /** Short form used in running prose, where the full firm reads as boilerplate. */
  shortName: "Noventis d.o.o.",
  /** Trading / product name. */
  brand: "nReport",
  site: "www.nreport.ai",
  siteUrl: "https://www.nreport.ai",

  /** Registered seat (AJPES, entry of 24 March 2026). */
  street: "Ulica Eve Lovše 18",
  postalCode: "2000",
  city: "Maribor",
  country: "Slovenija",

  /** Matična številka (AJPES registration number), 10 digits. */
  registrationNumber: "7472030000",
  /**
   * Davčna številka. NOT prefixed with "SI" — that prefix denotes an
   * "ID za DDV", which this company does not hold. See `vatRegistered`.
   */
  vatNumber: "81239700",
  /**
   * False: the company is not registered for VAT (mali davčni zavezanec).
   *
   * This is not a cosmetic flag. It decides whether prices may be advertised
   * as VAT-inclusive and whether invoices must carry the exemption note under
   * ZDDV-1, 94. člen. Flip it only together with an actual VAT registration,
   * and then set `vatNumber` to the "SI"-prefixed ID za DDV.
   */
  vatRegistered: false,
  /** Registrski organ + vpis, e.g. "Okrožno sodišče v Mariboru, srg. …". */
  registryEntry: "", // TODO(legal): from the company's register extract
  /** Osnovni kapital, e.g. "7.500,00 EUR". */
  shareCapital: "", // TODO(legal): from the company's register extract
  /** Zastopnik / direktor. Three are registered; not readable from public sources. */
  representative: "", // TODO(legal)

  /** Contact addresses published in the legal pages. */
  emailGeneral: "info@nreport.ai",
  emailPrivacy: "privacy@nreport.ai",
  emailCookies: "privacy@nreport.ai",
  /** Optional — omitted from the rendered block when empty. */
  phone: "",
} as const;

/** True when the seat address is complete enough to print. */
export const hasAddress = (): boolean =>
  Boolean(COMPANY.street && COMPANY.postalCode && COMPANY.city);

/** "Ulica 1, 1000 Ljubljana, Slovenija" — empty string when not yet filled in. */
export const companyAddress = (): string =>
  hasAddress()
    ? `${COMPANY.street}, ${COMPANY.postalCode} ${COMPANY.city}, ${COMPANY.country}`
    : "";

/** Labels for the provider-identification block, supplied per locale. */
export interface CompanyLabels {
  legalName: string;
  address: string;
  registrationNumber: string;
  /** Used when the company holds an ID za DDV. */
  vatNumber: string;
  /** Used when it does not — a davčna številka is not a VAT ID and mislabelling
   *  it as one is a representation that the company charges VAT. */
  taxNumber: string;
  registryEntry: string;
  shareCapital: string;
  representative: string;
  email: string;
  phone: string;
  website: string;
}

/** The correct label for the tax identifier this company actually holds. */
const taxIdLabel = (labels: CompanyLabels): string =>
  COMPANY.vatRegistered ? labels.vatNumber : labels.taxNumber;

/**
 * Provider identification as label/value rows, with unfilled fields dropped.
 * Used by the `company` legal block and by the footer.
 */
export function companyRows(labels: CompanyLabels): Array<[string, string]> {
  const rows: Array<[string, string]> = [[labels.legalName, COMPANY.legalName]];
  const address = companyAddress();
  if (address) rows.push([labels.address, address]);
  if (COMPANY.registrationNumber)
    rows.push([labels.registrationNumber, COMPANY.registrationNumber]);
  if (COMPANY.vatNumber) rows.push([taxIdLabel(labels), COMPANY.vatNumber]);
  if (COMPANY.registryEntry) rows.push([labels.registryEntry, COMPANY.registryEntry]);
  if (COMPANY.shareCapital) rows.push([labels.shareCapital, COMPANY.shareCapital]);
  if (COMPANY.representative) rows.push([labels.representative, COMPANY.representative]);
  rows.push([labels.email, COMPANY.emailGeneral]);
  if (COMPANY.phone) rows.push([labels.phone, COMPANY.phone]);
  rows.push([labels.website, COMPANY.site]);
  return rows;
}

/** One-line form for the footer: "Noventis d.o.o., Ulica 1, 1000 Ljubljana · MŠ: … · ID za DDV: …" */
export function companyFooterLine(labels: CompanyLabels): string {
  const parts: string[] = [COMPANY.legalName];
  const address = companyAddress();
  if (address) parts.push(address);
  if (COMPANY.registrationNumber)
    parts.push(`${labels.registrationNumber}: ${COMPANY.registrationNumber}`);
  if (COMPANY.vatNumber) parts.push(`${taxIdLabel(labels)}: ${COMPANY.vatNumber}`);
  return parts.join(" · ");
}
